"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CommonValidator = exports.BaseValidator = void 0;
exports.runValidators = runValidators;
exports.createChildOptions = createChildOptions;
const ramda_1 = require("ramda");
const context_1 = require("./context");
const utils_1 = require("./utils");
class BaseValidator {
    async validate(value, options = {}) {
        const ctx = context_1.Context.fromValue(value);
        await this.entry({ rootCtx: ctx, ctx, options });
        return ctx.toResult();
    }
}
exports.BaseValidator = BaseValidator;
const defaultProps = {
    presence: "required"
};
class CommonValidator extends BaseValidator {
    props;
    constructor(props = {}) {
        super();
        this.props = { ...defaultProps, ...props };
    }
    getAllPossibleInterpretations() {
        return this.props.interpretations || [];
    }
    getAllPossibleTags() {
        return this.props.tags || [];
    }
    mergeBaseProps(props) {
        return this.merge(props);
    }
    interpretation(interpretation) {
        return this.mergeBaseProps((0, utils_1.pushInterpretation)(this.props, interpretation));
    }
    tag(tag) {
        return this.mergeBaseProps((0, utils_1.pushTag)(this.props, tag));
    }
    message(details) {
        return this.mergeBaseProps({
            messages: [...(this.props.messages || []), details]
        });
    }
    expiration(expiration) {
        return this.mergeBaseProps({ expiration });
    }
    unit(unit) {
        return this.mergeBaseProps({ unit });
    }
    default(value) {
        return this.mergeBaseProps({ default: value });
    }
    presence(presence) {
        return this.mergeBaseProps({ presence });
    }
    required() {
        return this.presence("required");
    }
    optional() {
        return this.presence("optional");
    }
    forbidden() {
        return this.presence("forbidden");
    }
    async entry({ rootCtx, ctx, options }) {
        await runValidators({
            rootCtx,
            ctx,
            options,
            validators: [
                this.logProps,
                this.logCustom,
                this.applyDefault,
                this.validatePresence,
                this.runValidateValue
            ]
        });
    }
    logProps = async ({ ctx, next }) => {
        const { messages = [], ...props } = this.getPropsToLog();
        ctx.addMessageWithPath(props);
        for (const message of messages) {
            ctx.addMessageWithPath(message);
        }
        return next();
    };
    getPropsToLog() {
        const out = {};
        for (const [key, value] of Object.entries(this.props)) {
            out[key] = value;
        }
        return out;
    }
    logCustom = async ({ next }) => {
        return next();
    };
    applyDefault = async ({ rootCtx, ctx, next, options }) => {
        const { default: defaultValue } = this.props;
        if (options.applyDefaults !== false &&
            ctx.value === undefined &&
            defaultValue !== undefined) {
            const value = await (0, utils_1.evaluateValue)(rootCtx, ctx, defaultValue);
            ctx.setValue(value);
        }
        return next();
    };
    validatePresence = async ({ rootCtx, ctx, next }) => {
        let presence = await (0, utils_1.evaluateValue)(rootCtx, ctx, this.props.presence);
        if (presence === undefined) {
            presence = "required";
        }
        if (presence === "optional") {
            return this.validateOptional(ctx, next);
        }
        if (presence === "required") {
            return this.validateRequired(ctx, next);
        }
        return this.validateForbidden(ctx);
    };
    validateOptional = async (ctx, next) => {
        if (!this.isEmptyValue(ctx.value)) {
            return next();
        }
    };
    validateRequired = async (ctx, next) => {
        if (!this.isEmptyValue(ctx.value)) {
            return next();
        }
        ctx.addError({ presence: "required" });
    };
    validateForbidden = async (ctx) => {
        if (this.isEmptyValue(ctx.value)) {
            return;
        }
        if (ctx.path.length !== 0 && !(0, ramda_1.hasPath)(ctx.path, ctx.root)) {
            return;
        }
        ctx.addError({ presence: "forbidden" });
    };
    runValidateValue = async (args) => {
        return this.validateValue(args);
    };
    isEmptyValue(value) {
        return value === undefined || value === null;
    }
}
exports.CommonValidator = CommonValidator;
async function runValidators({ rootCtx, ctx, options, validators }) {
    let prevIndex = -1;
    function next(i) {
        if (prevIndex > i) {
            return Promise.reject(new Error("next() called multiple times"));
        }
        prevIndex = i;
        const fn = validators[i];
        if (fn === undefined) {
            return Promise.resolve();
        }
        try {
            return fn({ rootCtx, ctx, next: () => next(i + 1), options });
        }
        catch (e) {
            return Promise.reject(e);
        }
    }
    return next(0);
}
function createChildOptions(optionsA, optionsB) {
    const options = { ...optionsA };
    if (optionsB.applyDefaults !== undefined) {
        options.applyDefaults = optionsB.applyDefaults;
    }
    return options;
}
